import React, { useEffect, useState } from "react";
import { Button } from "antd";
import {
  WifiOutlined,
  DisconnectOutlined,
  LoadingOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useAuthLayoutContext } from "../../context/AuthLayoutContext";
import { SOCKET_STATUS, MAX_RETRIES_LIMIT } from "../../api/webSocketRx";

const ConnectionToast = () => {
  const { connectionState } = useAuthLayoutContext();
  const { status, retryCount } = connectionState;

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (
      status === SOCKET_STATUS.DISCONNECTED ||
      status === SOCKET_STATUS.CONNECTING
    ) {
      if (retryCount > 0) {
        setVisible(true);
      }
    } else if (status === SOCKET_STATUS.CONNECTED) {
      if (visible) {
        const timer = setTimeout(() => {
          setVisible(false);
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [status, retryCount]);

  const isFailed = retryCount >= MAX_RETRIES_LIMIT;

  const handleReload = () => {
    window.location.reload();
  };

  const getConfig = () => {
    if (status === SOCKET_STATUS.CONNECTED) {
      return {
        bg: "bg-gray-800",
        border: "border-l-4 border-green-500",
        icon: <WifiOutlined className="text-green-500 text-xl" />,
        text: "Internet connection restored.",
        subText: null,
      };
    }

    if (isFailed) {
      return {
        bg: "bg-gray-800",
        border: "border-l-4 border-red-500",
        icon: <DisconnectOutlined className="text-red-500 text-xl" />,
        text: "Unable to connect to server.",
        subText: "Please reload the page.",
      };
    }

    if (
      status === SOCKET_STATUS.CONNECTING ||
      status === SOCKET_STATUS.DISCONNECTED
    ) {
      return {
        bg: "bg-gray-800",
        border: "border-l-4 border-yellow-500",
        icon: <LoadingOutlined className="text-yellow-500 text-xl" />,
        text: "Trying to reconnect...",
        subText: `Try again ${retryCount}...`,
      };
    }

    return null;
  };

  const config = getConfig();
  if (!config) return null;

  return (
    <div
      className={`
        fixed bottom-6 right-6 z-[9999] 
        flex items-center gap-3 p-4 rounded-lg shadow-2xl 
        transition-all duration-500 ease-in-out transform
        ${config.bg} text-white min-w-[300px] max-w-[400px]
        ${config.border}
        ${visible ? "translate-y-0 opacity-100" : "translate-y-20 opacity-0 pointer-events-none"}
      `}
    >
      <div className="flex-shrink-0">{config.icon}</div>

      <div className="flex-grow flex flex-col">
        <span className="font-medium text-sm text-gray-100">{config.text}</span>
        {config.subText && (
          <span className="text-xs text-gray-400 mt-0.5">{config.subText}</span>
        )}
      </div>

      {isFailed && (
        <div className="flex-shrink-0 pl-2 border-l border-gray-600 ml-2">
          <Button
            type="link"
            size="small"
            onClick={handleReload}
            className="!text-blue-400 hover:!text-blue-300 !font-bold !p-0 flex items-center gap-1"
          >
            <ReloadOutlined /> Reload
          </Button>
        </div>
      )}
    </div>
  );
};

export default ConnectionToast;
